import { useEffect, useRef } from 'react';

// Keyboard coding (wf-adj-item-blind-1): a letter picks UT (Q, P, E, O; F or R then y, s or q),
// 1–5 pick RC in codebook order, U toggles unsure, Enter confirms. Keys typed into a field, with a
// modifier, or while a dialog is open are left alone.
const isTyping = (target) => {
    const tag = target?.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
};

const useCodingKeys = ({ enabled = true, utCodes = [], rcCodes = [], onUt, onRc, onUnsure, onEnter }) => {
    const prefix = useRef(null);
    const handlers = useRef({});
    handlers.current = { utCodes, rcCodes, onUt, onRc, onUnsure, onEnter };

    useEffect(() => {
        if (!enabled) return undefined;
        const onKey = (event) => {
            if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
            if (document.querySelector('[role="dialog"]')) return;
            const h = handlers.current;
            const key = event.key.toLowerCase();

            if (key === 'enter') {
                if (h.onEnter) {
                    event.preventDefault();
                    h.onEnter();
                }
                return;
            }
            if (/^[1-9]$/.test(key) && h.onRc) {
                const code = h.rcCodes[Number(key) - 1];
                if (code) h.onRc(code);
                return;
            }
            if (prefix.current && ['y', 's', 'q'].includes(key)) {
                const code = `${prefix.current}${key}`;
                prefix.current = null;
                if (h.utCodes.includes(code) && h.onUt) h.onUt(code);
                return;
            }
            if ((key === 'f' || key === 'r') && h.utCodes.some((c) => c.startsWith(key.toUpperCase()))) {
                prefix.current = key.toUpperCase();
                return;
            }
            prefix.current = null;
            if (key === 'u' && h.onUnsure) {
                h.onUnsure();
                return;
            }
            const single = key.toUpperCase();
            if (h.utCodes.includes(single) && h.onUt) h.onUt(single);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [enabled]);
};

export default useCodingKeys;
